import React, { useMemo, useState } from "react";
import {
  Sprout, ArrowRight, Check, Wheat, TrendingUp, CloudRain, Droplets, Bot,
  ArrowLeft, Sparkles, Sun, Thermometer, LineChart as LineIcon,
  Warehouse, ShieldAlert, ArrowDownCircle, CalendarClock, Coins, Snowflake, Flame,
} from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine,
} from "recharts";
import { useSEO } from "../lib/useSEO";
import ContactSalesDialog from "../shared/ContactSalesDialog";

const IMG_FARM = "https://images.unsplash.com/photo-1779043151929-e57da43e83b8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

// Deterministic pseudo-random so the demo curve is stable across renders
function seeded(seed) {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

const COMMODITIES = [
  { key: "corn", label: "Corn", unit: "$/bu", base: 4.42, vol: 0.06, trend: 0.004, insight: "Tightening stocks-to-use and strong export demand point to firmer nearby prices — hold a portion of old-crop bushels." },
  { key: "soybeans", label: "Soybeans", unit: "$/bu", base: 11.35, vol: 0.14, trend: -0.003, insight: "South American harvest pressure is capping rallies — consider forward-pricing a slice of new crop on bounces." },
  { key: "wheat", label: "Wheat", unit: "$/bu", base: 5.85, vol: 0.08, trend: 0.005, insight: "Dryness in key growing regions is adding a weather premium — momentum favors patient sellers." },
  { key: "cattle", label: "Live Cattle", unit: "$/lb", base: 1.86, vol: 0.02, trend: 0.002, insight: "Tight herd numbers keep the trend supportive — protect downside with options ahead of report weeks." },
  { key: "milk", label: "Class III Milk", unit: "$/cwt", base: 18.4, vol: 0.35, trend: 0.006, insight: "Firm cheese demand is lifting the curve — a good window to lock in margin coverage." },
];

function buildSeries(c) {
  const rnd = seeded(c.base * 1000 + c.label.length);
  const HIST = 24, FCAST = 8;
  const rows = [];
  let price = c.base;
  for (let i = 0; i < HIST; i++) {
    // gentle drift + noise with mean-reversion toward base so prices stay realistic
    const target = c.base * (1 + c.trend * i);
    price = price + (target - price) * 0.3 + (rnd() - 0.5) * c.vol;
    rows.push({ w: `W${i + 1}`, price: +price.toFixed(2) });
  }
  const last = rows[rows.length - 1].price;
  rows[rows.length - 1].forecast = last;
  rows[rows.length - 1].lo = last;
  rows[rows.length - 1].hi = last;
  let f = last;
  for (let i = 0; i < FCAST; i++) {
    const target = c.base * (1 + c.trend * (HIST + i));
    f = f + (target - f) * 0.3 + (rnd() - 0.45) * c.vol * 0.6;
    const spread = c.vol * (1.4 + i * 0.4);
    rows.push({
      w: `W${HIST + i + 1}`,
      forecast: +f.toFixed(2),
      lo: +(f - spread).toFixed(2),
      hi: +(f + spread).toFixed(2),
    });
  }
  const first = rows[0].price;
  const change = ((last - first) / first) * 100;
  return { rows, last, change, splitAt: `W${HIST}` };
}

function FuturesDemo() {
  const [active, setActive] = useState(COMMODITIES[0]);
  const { rows, last, change, splitAt } = useMemo(() => buildSeries(active), [active]);
  const up = change >= 0;

  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5 sm:p-7">
      {/* commodity tabs */}
      <div className="flex flex-wrap gap-2">
        {COMMODITIES.map((c) => (
          <button
            key={c.key}
            data-testid={`futures-tab-${c.key}`}
            onClick={() => setActive(c)}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-all ${
              active.key === c.key ? "bg-ai text-white" : "border border-white/15 text-white/70 hover:bg-white/10"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* price header */}
      <div className="mt-6 flex items-end justify-between gap-4">
        <div>
          <div className="text-sm text-white/50">{active.label} · near-month futures</div>
          <div className="mt-1 flex items-baseline gap-3">
            <span data-testid="futures-price" className="font-heading text-4xl font-extrabold">${last.toFixed(2)}</span>
            <span className="text-sm text-white/50">{active.unit}</span>
            <span className={`text-sm font-bold ${up ? "text-action-keep" : "text-action-remove"}`}>
              {up ? "▲" : "▼"} {Math.abs(change).toFixed(1)}% <span className="text-white/40 font-normal">/ 24 wk</span>
            </span>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1 text-xs text-white/60">
          <LineIcon className="h-3.5 w-3.5 text-ai" /> Live-style demo data
        </div>
      </div>

      {/* chart */}
      <div className="mt-4 h-72 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
            <defs>
              <linearGradient id="bandFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#FF5E00" stopOpacity={0.22} />
                <stop offset="100%" stopColor="#FF5E00" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis dataKey="w" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} tickLine={false} axisLine={false} interval={3} />
            <YAxis tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} tickLine={false} axisLine={false} domain={["auto", "auto"]} width={44} />
            <Tooltip
              contentStyle={{ background: "#0B1510", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, color: "#fff", fontSize: 12 }}
              formatter={(v, n) => [`$${Number(v).toFixed(2)}`, n === "price" ? "Actual" : n === "forecast" ? "Forecast" : n === "hi" ? "Upper" : "Lower"]}
            />
            <ReferenceLine x={splitAt} stroke="rgba(255,255,255,0.25)" strokeDasharray="4 4" label={{ value: "forecast →", fill: "rgba(255,255,255,0.4)", fontSize: 10, position: "insideTopRight" }} />
            <Area type="monotone" dataKey="hi" stroke="none" fill="url(#bandFill)" connectNulls />
            <Area type="monotone" dataKey="lo" stroke="none" fill="#0B1510" connectNulls />
            <Line type="monotone" dataKey="price" stroke="#fff" strokeWidth={2.4} dot={false} connectNulls />
            <Line type="monotone" dataKey="forecast" stroke="#FF5E00" strokeWidth={2.4} strokeDasharray="6 4" dot={false} connectNulls />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* AI insight */}
      <div data-testid="futures-insight" className="mt-4 flex items-start gap-3 rounded-2xl border border-ai/25 bg-ai/[0.06] p-4">
        <span className="h-8 w-8 rounded-lg bg-ai/15 text-ai flex items-center justify-center shrink-0"><Bot className="h-5 w-5" /></span>
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-ai">AI market brief</div>
          <p className="mt-1 text-sm text-white/75 leading-relaxed">{active.insight}</p>
        </div>
      </div>
    </div>
  );
}

const ADVISOR_ROWS = [
  { c: "Corn", cat: "Grains · $/bu", rec: "STORE", net: "+3.2%", fwd: "+5.8%", analog: "2019", reason: "Carry + expected basis gain more than cover storage and interest — hold and sell the carry." },
  { c: "Soybeans", cat: "Oilseeds · $/bu", rec: "HEDGE", net: "+1.9%", fwd: "−1.2%", analog: "2021", reason: "Forward easing but storing still pays — store the crop and lock price with a hedge." },
  { c: "Wheat", cat: "Grains · $/bu", rec: "SELL_NOW", net: "−2.4%", fwd: "−3.6%", analog: "2015", reason: "Storing won't cover carry — move it off the combine." },
  { c: "Live Cattle", cat: "Livestock · $/lb", rec: "HEDGE", net: "—", fwd: "+2.1%", analog: "2022", reason: "Historically strong — lock margin with a futures/options hedge." },
];
const REC = {
  STORE: { label: "Store", icon: Warehouse, cls: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30" },
  HEDGE: { label: "Hedge", icon: ShieldAlert, cls: "bg-ai/15 text-ai border-ai/30" },
  SELL_NOW: { label: "Sell now", icon: ArrowDownCircle, cls: "bg-sky-500/15 text-sky-300 border-sky-400/30" },
};

function AdvisorDemo() {
  const [active, setActive] = useState(ADVISOR_ROWS[0].c);
  const activeRow = ADVISOR_ROWS.find((r) => r.c === active) || ADVISOR_ROWS[0];
  return (
    <div className="grid lg:grid-cols-5 gap-5">
      {/* Advisor table */}
      <div className="lg:col-span-3 rounded-3xl border border-white/10 bg-white/[0.03] p-5 sm:p-6">
        <div className="flex items-center gap-2">
          <span className="h-8 w-8 rounded-lg bg-ai/15 text-ai flex items-center justify-center"><Coins className="h-5 w-5" /></span>
          <div>
            <div className="font-heading font-bold">Basis & Storage Advisor</div>
            <div className="text-[11px] text-white/45">Sell now · Store · Hedge — per commodity</div>
          </div>
        </div>
        <div className="mt-4 overflow-hidden rounded-xl border border-white/10">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-white/[0.04] text-left text-[10px] uppercase tracking-wider text-white/45">
                <th className="py-2.5 px-3">Commodity</th>
                <th className="px-2">Call</th>
                <th className="px-2 text-right">Fwd</th>
                <th className="px-2 text-right">Net store</th>
                <th className="px-3 text-right hidden sm:table-cell">Analog</th>
              </tr>
            </thead>
            <tbody>
              {ADVISOR_ROWS.map((r) => {
                const m = REC[r.rec];
                const Ic = m.icon;
                const on = r.c === active;
                const neg = r.net.startsWith("−");
                return (
                  <tr key={r.c} onClick={() => setActive(r.c)}
                    data-testid={`advisor-row-${r.c.toLowerCase().replace(/\s/g, "-")}`}
                    className={`border-t border-white/8 cursor-pointer transition-colors ${on ? "bg-ai/[0.06]" : "hover:bg-white/[0.03]"}`}>
                    <td className="py-3 px-3 font-medium">{r.c}<div className="text-[10px] text-white/40 font-normal">{r.cat}</div></td>
                    <td className="px-2"><span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${m.cls}`}><Ic className="h-3 w-3" />{m.label}</span></td>
                    <td className="px-2 text-right font-mono text-white/60">{r.fwd}</td>
                    <td className={`px-2 text-right font-mono font-semibold ${neg ? "text-sky-300" : r.net === "—" ? "text-white/40" : "text-emerald-300"}`}>{r.net}</td>
                    <td className="px-3 text-right font-mono text-white/50 hidden sm:table-cell">{r.analog}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div data-testid="advisor-reason" className="mt-3 flex items-start gap-2.5 rounded-xl border border-ai/25 bg-ai/[0.06] p-3.5">
          <span className="h-7 w-7 rounded-lg bg-ai/15 text-ai flex items-center justify-center shrink-0"><Bot className="h-4 w-4" /></span>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-ai">{activeRow.c} · why</div>
            <p className="mt-0.5 text-sm text-white/75 leading-relaxed">{activeRow.reason}</p>
          </div>
        </div>
        <p className="mt-2 text-[10px] text-white/35">Illustrative demo. Live futures + your modeled basis and carry cost drive the real recommendation.</p>
      </div>

      {/* Weather risk + analog side cards */}
      <div className="lg:col-span-2 space-y-5">
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-amber-400" />
            <span className="font-heading font-bold text-sm">Weather risk · Des Moines, IA</span>
          </div>
          {[
            { l: "Drought", Ic: Sun, v: 62, c: "#f59e0b" },
            { l: "Heat stress", Ic: Flame, v: 38, c: "#ef4444" },
            { l: "Frost", Ic: Snowflake, v: 8, c: "#0ea5e9" },
          ].map((x) => (
            <div key={x.l} className="mt-3">
              <div className="flex items-center gap-1.5 text-[11px] text-white/55"><x.Ic className="h-3 w-3" />{x.l}<span className="ml-auto font-mono text-white/80">{x.v}%</span></div>
              <div className="mt-1 h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${x.v}%`, background: x.c }} /></div>
            </div>
          ))}
        </div>
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
          <div className="flex items-center gap-2"><CalendarClock className="h-5 w-5 text-ai" /><span className="font-heading font-bold text-sm">Historical analog year</span></div>
          <div className="mt-2 flex items-end gap-2.5">
            <span className="font-heading text-3xl font-extrabold">2019</span>
            <span className="mb-1 rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-mono">94% match</span>
          </div>
          <p className="mt-2 text-[11px] text-white/55 leading-relaxed">This growing season most resembles 2019 — a similar heat build-up — to frame your yield and price outlook.</p>
        </div>
      </div>
    </div>
  );
}

const AG_TOOLS = [
  { icon: TrendingUp, title: "Live commodity futures", body: "Grain, livestock and dairy prices from real market feeds — plan grow, hold and sell decisions against the actual curve." },
  { icon: CloudRain, title: "Weather-driven yield", body: "Location forecasts turn into yield multipliers and revenue projections for every facility, updated continuously." },
  { icon: Droplets, title: "Soil moisture & drought watch", body: "Soil-moisture tracking and automatic drought alerts flag at-risk facilities before they hit your bottom line." },
  { icon: Bot, title: "AI market briefs & alerts", body: "A plain-English brief plus custom price alerts tell you what moved, why it matters, and what to do next." },
  { icon: Wheat, title: "Production-mix optimization", body: "Rank commodities and varieties by margin, yield and market outlook — see what to expand, hold and scale back." },
  { icon: Sun, title: "Per-facility outlook", body: "Break yield and revenue projections down by facility, not just by commodity group, so every operation is planned." },
];

export default function FarmersLanding({ onGetStarted, onLogin, onHome }) {
  const login = onLogin || onGetStarted;
  const [talkOpen, setTalkOpen] = useState(false);
  useSEO({
    title: "AssortIQ for Agriculture — Commodity Futures, Weather & Crop Planning for Farmers",
    description: "A commodity price forecasting and grain-marketing tool for farmers: live grain, livestock & dairy futures, a Basis & Storage Advisor that tells you when to sell, store or hedge, weather-driven yield and soil-moisture signals, drought alerts and AI market briefs. Start a free trial.",
    canonical: "https://getassortiq.com/farmers",
  });
  return (
    <div className="min-h-screen bg-forest-900 text-white font-body antialiased overflow-x-hidden">
      <ContactSalesDialog open={talkOpen} onOpenChange={setTalkOpen} source="farmers-landing" vertical="agriculture" title="Talk to us" />
      {/* NAV */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-forest-900/80 border-b border-white/10">
        <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
          <button data-testid="farmers-home" onClick={onHome} className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-md bg-ai flex items-center justify-center"><Sprout className="h-5 w-5" strokeWidth={2.4} /></div>
            <span className="font-heading font-extrabold text-xl">Assort<span className="text-ai">IQ</span></span>
          </button>
          <div className="flex items-center gap-3">
            <button data-testid="farmers-back" onClick={onHome} className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-white/70 hover:text-white">
              <ArrowLeft className="h-4 w-4" /> All industries
            </button>
            <button data-testid="farmers-login" onClick={login} className="text-sm font-semibold text-white/80 hover:text-white">Log in</button>
            <button data-testid="farmers-nav-cta" onClick={onGetStarted} className="group flex items-center gap-1.5 rounded-full bg-ai px-4 py-2 text-sm font-bold hover:bg-[#e05400] transition-all hover:-translate-y-0.5">
              Start free trial <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,94,0,0.16),transparent_55%)]" />
        <div className="relative mx-auto max-w-7xl px-6 pt-16 pb-12 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-ai/40 bg-ai/10 px-3 py-1 text-xs font-semibold text-ai uppercase tracking-wider">
              <Wheat className="h-3.5 w-3.5" /> AssortIQ for Agriculture
            </div>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05]">
              Plant, hold & sell<br /><span className="text-ai">on real market data.</span>
            </h1>
            <p className="mt-5 text-white/70 text-base sm:text-lg max-w-xl leading-relaxed">
              AssortIQ brings live commodity futures, weather-driven yield signals and AI market briefs into one place —
              so producers optimize their production mix against the actual market, not last season's guess.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button data-testid="farmers-hero-cta" onClick={onGetStarted} className="group flex items-center gap-2 rounded-full bg-ai px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#e05400] transition-all hover:-translate-y-0.5">
                Start 7-day free trial <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
              <button type="button" data-testid="farmers-talk-to-us" onClick={() => setTalkOpen(true)} className="rounded-full border border-white/25 px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-white/10 transition-colors">
                Talk to us
              </button>
            </div>
            <p className="mt-4 text-xs text-white/50 flex items-center gap-2"><Check className="h-4 w-4 text-action-keep" /> No charge today · Set up in minutes · Built for row crop, livestock & dairy</p>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-ai/20 blur-3xl rounded-full" />
            <img src={IMG_FARM} alt="Farmer inspecting a golden wheat field" className="relative rounded-2xl border border-white/10 shadow-2xl w-full h-80 object-cover" />
          </div>
        </div>
      </section>

      {/* FUTURES DEMO */}
      <section className="mx-auto max-w-7xl px-6 py-12">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-ai/10 text-ai px-3 py-1 text-xs font-bold uppercase tracking-wider">
            <Sparkles className="h-3.5 w-3.5" /> Live-style demo
          </div>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-extrabold">See the futures desk in action</h2>
          <p className="mt-3 text-white/60">Switch commodities to watch the price history, the AI forecast with confidence bands, and a plain-English market brief update live.</p>
        </div>
        <div className="mt-8"><FuturesDemo /></div>
      </section>

      {/* BASIS & STORAGE ADVISOR */}
      <section className="mx-auto max-w-7xl px-6 py-12" id="basis-advisor">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-ai/10 text-ai px-3 py-1 text-xs font-bold uppercase tracking-wider">
            <Coins className="h-3.5 w-3.5" /> New · Grain marketing
          </div>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-extrabold">Sell now, store, or hedge? Stop guessing.</h2>
          <p className="mt-3 text-white/60">
            The Basis & Storage Advisor runs the basis-plus-carry math for every commodity you grow — weighing the forward curve,
            your local basis and true storage cost — and returns a clear <span className="text-white/80 font-semibold">Sell now / Store / Hedge</span> call
            with the net per-bushel behind it, plus per-facility weather risk and the season's closest historical analog.
          </p>
        </div>
        <div className="mt-8"><AdvisorDemo /></div>
        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
          <span className="text-white/45">Learn the framework:</span>
          <a href="/blog/store-or-sell-grain" className="font-semibold text-ai hover:underline">Store or sell your grain?</a>
          <a href="/blog/understanding-grain-basis" className="font-semibold text-ai hover:underline">Understanding basis</a>
          <a href="/blog/hedging-vs-storing-grain" className="font-semibold text-ai hover:underline">Hedging vs. storing</a>
          <a href="/tools/grain-storage-roi-calculator" className="font-semibold text-white/70 hover:text-white">Free storage ROI calculator →</a>
        </div>
      </section>

      {/* EXAMPLE RESULTS */}
      <section className="mx-auto max-w-7xl px-6 py-10">
        <div className="rounded-3xl border border-ai/25 bg-gradient-to-br from-ai/[0.08] to-transparent p-8 sm:p-10">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-ai/10 text-ai px-3 py-1 text-xs font-bold uppercase tracking-wider"><Sparkles className="h-3.5 w-3.5" /> Example results</div>
            <h2 className="mt-4 font-heading text-2xl sm:text-3xl font-extrabold">What marketing your crop on data can look like</h2>
          </div>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="farmers-results">
            {[
              { n: "+$0.15/bu", l: "illustrative sell-timing edge" },
              { n: "−24%", l: "avoidable storage carry" },
              { n: "Every crop", l: "gets a sell / store / hedge call" },
              { n: "Per-facility", l: "drought, heat & frost risk" },
            ].map((s) => (
              <div key={s.l} className="text-center sm:text-left">
                <div className="font-heading text-4xl sm:text-5xl font-extrabold text-ai">{s.n}</div>
                <div className="mt-1 text-sm text-white/60">{s.l}</div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-[11px] text-white/40">Illustrative outcomes based on the decisions AssortIQ helps you make — your results depend on your operation and the market.</p>
        </div>
      </section>

      {/* TOOLS GRID */}
      <section className="bg-white/[0.02] border-y border-white/10">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="max-w-2xl">
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold">Everything a producer needs to plan a season</h2>
            <p className="mt-3 text-white/60">One platform for market prices, weather risk and production decisions — no data team required.</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {AG_TOOLS.map((t) => (
              <div key={t.title} data-testid={`ag-tool-${t.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:border-ai/40 hover:-translate-y-1 transition-all">
                <div className="h-11 w-11 rounded-xl bg-ai/15 text-ai flex items-center justify-center"><t.icon className="h-6 w-6" /></div>
                <h3 className="mt-4 font-heading text-lg font-bold">{t.title}</h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WEATHER STRIP */}
      <section className="mx-auto max-w-7xl px-6 py-16">
        <div className="grid sm:grid-cols-3 gap-6 text-center">
          {[
            { icon: Thermometer, n: "Per-facility", l: "yield multipliers from live weather" },
            { icon: Droplets, n: "Drought watch", l: "automatic alerts on at-risk facilities" },
            { icon: TrendingUp, n: "5 commodities", l: "grains, livestock & dairy tracked" },
          ].map((s) => (
            <div key={s.l} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <s.icon className="h-7 w-7 text-ai mx-auto" />
              <div className="mt-3 font-heading text-2xl font-extrabold text-ai">{s.n}</div>
              <div className="mt-1 text-sm text-white/60">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 pb-20">
        <div className="relative overflow-hidden rounded-3xl border border-ai/30 bg-gradient-to-br from-ai/20 to-transparent p-10 sm:p-14 text-center">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,94,0,0.15),transparent_70%)]" />
          <div className="relative">
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold">Ready to plan your next season with data?</h2>
            <p className="mt-3 text-white/70 max-w-xl mx-auto">Start free — see live futures, weather and your optimized production mix in the first session.</p>
            <button data-testid="farmers-footer-cta" onClick={onGetStarted} className="mt-7 inline-flex items-center gap-2 rounded-full bg-ai px-7 py-3.5 text-sm font-bold uppercase tracking-wider hover:bg-[#e05400] transition-all hover:-translate-y-0.5">
              Start 7-day free trial <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <button onClick={onHome} className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-md bg-ai flex items-center justify-center"><Sprout className="h-4 w-4" strokeWidth={2.4} /></div>
            <span className="font-heading font-extrabold">Assort<span className="text-ai">IQ</span></span>
          </button>
          <div className="text-xs text-white/40">© {new Date().getFullYear()} AssortIQ. Built for grocery, retail & agriculture.</div>
        </div>
      </footer>
    </div>
  );
}
